import { defineStore } from 'pinia';
import type { CartItem } from '@/Types/CartItem';
import type { Item } from '@/Types/Item';
import {usePage} from "@inertiajs/vue3";
import {ItemVariant} from "@/Types/ItemVariant";

const page = usePage();
export const useCartStore = defineStore('cart', {
    state: () => ({
        cartItems: undefined as CartItem[] | undefined,
        loading: true,
        isInitialized: false,
    }),
    getters: {
        getNumItems: (state) => state.cartItems?.length ?? 0,
        getTotal: (state) => {
            let total = 0;
            state.cartItems.forEach((cartItem) => {
                total += cartItem.item.price * (cartItem.quantity ?? 1);
            });
            return total;
        },
        hasGoalItems: (state) => {
            if (!state.cartItems) return false;
            return state.cartItems.some(cartItem => cartItem.item.goalContribution > 0);
        }
    },
    actions: {
        async initialize() {
            if (this.isInitialized) return;
            this.loading = true;
            await this.refresh();
            this.loading = false;
            this.isInitialized = true;
        },

        async refresh() {
            this.loading = true;

            this.cartItems = await refreshCart();

            this.loading = false;
        },

        async addItem(item: Item, variant: ItemVariant | undefined, quantity: number) {
            this.loading = true;

            const itemInCart = this.getItemIndexInCart(item, variant);

            if (itemInCart !== undefined) {
                const newQuantity = this.cartItems[itemInCart].quantity + quantity;
                await this.updateQuantity(itemInCart, newQuantity);
                return;
            }
            this.cartItems = await addToCart(item, variant, quantity);

            this.loading = false;
        },

        async deleteByIndex(index: number) {
            this.loading = true;

            const req = await updateQuantity(index, 0);
            this.cartItems = req;

            this.loading = false;
        },

        async updateQuantity(index: number, quantity: number) {
            this.loading = true;

            const req = await updateQuantity(index, quantity);
            this.cartItems = req;

            this.loading = false;

        },

        getItemIndexInCart(item: Item, variant: ItemVariant | undefined): number | undefined {
            const foundItem = this.cartItems.find((el: CartItem) => {
                return el.item.id === item.id && el.variant.id === variant.id;
            });
            if (foundItem) {
                return this.cartItems.indexOf(foundItem);
            }
            return undefined;
        }
    }
});

async function refreshCart(): Promise<CartItem[]> {
    const res = await fetch(route('cart.fetch'));
    const {status, payload} = await res.json();
    if (status !== 'OK') {
        throw new Error(payload);
    }
    return payload;
}

async function addToCart(item: Item, variant: ItemVariant | undefined, quantity: number) {
    const page = usePage();

    const thisRoute = route('shop.items.add_to_cart', {
        shop: page.props.shopSlug,
        item: item.slug
    });
    
    let variantId = variant ? variant.id : null;

    let res = await fetch(thisRoute, {
        method: 'POST',
        headers: {
            'X-CSRF-TOKEN': page.props.csrf,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            variantId,
            quantity
        }),
    });

    let {status, payload} = await res.json();
    if (status !== 'OK') {
        throw new Error(payload);
    }

    return payload;
}

async function updateQuantity(itemIndex: number, quantity: number) {
    const thisRoute = route('cart.update_quantity');
    let res = await fetch(thisRoute, {
        method: 'PATCH',
        headers: {
            'X-CSRF-TOKEN': page.props.csrf,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            itemIndex,
            quantity
        }),
    });

    let {status, payload} = await res.json();
    if (status !== 'OK') {
        throw new Error(payload);
    }

    return payload;
}
